import { DateTime } from 'luxon';
import type {
  IProductivityCustomRange,
  IProductivityTimeframe,
  ProductivityGranularity,
} from '@interfaces/IProductivity';

/**
 * Resolves a `TimeframeSelector` value into a `{ dateFrom, dateTo }` pair of
 * `YYYY-MM-DD` strings in the SITE's timezone — the shape the productivity BFF
 * expects.
 *
 * Returns `null` for `custom` until both ends are picked, so
 * callers can gate the fetch on a complete range.
 *
 * The non-custom timeframes are rolling windows ending today: this week = the
 * last 7 days, this month = the last month, this year = the last 12 months.
 *
 * `tz` is required, not defaulted — a silent JST default would shift every
 * window by a day on a non-JST site (same convention as `dateRules.ts`).
 */

export interface IProductivityRange {
  dateFrom: string;
  dateTo: string;
}

/** A picked Date (local midnight) → its `YYYY-MM-DD` calendar day. */
export function fmtLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// The inverse: a `YYYY-MM-DD` day → the local-midnight Date the picker would
// hand over for it, so `fmtLocal` gives the same string back.
function parseLocal(day: string): Date {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

/** `parseLocal` for a day that came from a URL: null unless it is a real
 *  calendar day (`2026-02-31` rolls over, so it does not round-trip). */
export function parseLocalDay(day: string | null): Date | null {
  if (day === null || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const parsed = parseLocal(day);
  return fmtLocal(parsed) === day ? parsed : null;
}

/** The custom picker's dates for a resolved range — what Custom opens on, so
 *  the resolved range (and the query key) is unchanged by the switch. */
export function customRangeFor(range: IProductivityRange): IProductivityCustomRange {
  return { from: parseLocal(range.dateFrom), to: parseLocal(range.dateTo) };
}

/** Custom ranges pick their bars by span: up to this many days, one bar a
 *  day; up to the next, one a week; beyond, one a month. */
const CUSTOM_DAILY_MAX_DAYS = 45;
const CUSTOM_WEEKLY_MAX_DAYS = 180;

/** The chart's bucket for a timeframe: a week or a month by day, a year by
 *  month, and a custom range by its span (a custom year is 12 bars, not 366). */
export function granularityForTimeframe(
  timeframe: IProductivityTimeframe,
  range: IProductivityRange | null = null,
): ProductivityGranularity {
  if (timeframe === 'thisYear') return 'month';
  if (timeframe !== 'custom' || !range) return 'day';
  const days = Math.round((Date.parse(range.dateTo) - Date.parse(range.dateFrom)) / 86_400_000) + 1;
  if (days <= CUSTOM_DAILY_MAX_DAYS) return 'day';
  return days <= CUSTOM_WEEKLY_MAX_DAYS ? 'week' : 'month';
}

// Gets the user selected timeframe and custom range
// Returns a range object with dateFrom and dateTo in YYYY-MM-DD format
export function resolveProductivityRange(
  timeframe: IProductivityTimeframe,
  customRange: IProductivityCustomRange,
  tz: string,
): IProductivityRange | null {
  if (timeframe === 'custom') {
    if (!customRange.from || !customRange.to) return null;
    const from = fmtLocal(customRange.from);
    const to = fmtLocal(customRange.to);
    return from <= to ? { dateFrom: from, dateTo: to } : { dateFrom: to, dateTo: from };
  }

  const today = DateTime.now().setZone(tz).startOf('day');
  let from: DateTime;
  switch (timeframe) {
    case 'thisWeek':
      from = today.minus({ days: 6 }); // last 7 days, inclusive
      break;
    case 'thisMonth':
      from = today.minus({ months: 1 }); // last month, one bar per day
      break;
    case 'thisYear':
      // 11 months back, snapped to the month start → exactly 12 monthly buckets.
      from = today.minus({ months: 11 }).startOf('month');
      break;
    default:
      from = today;
      break;
  }

  return { dateFrom: from.toFormat('yyyy-MM-dd'), dateTo: today.toFormat('yyyy-MM-dd') };
}
